/* global google */
import { useEffect, useState, useRef, useCallback } from 'react';

import { MarkerClusterer as GoogleMarkerClusterer } from '@googlemaps/markerclusterer';
import { useMap } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';

export default function MarkerClusterer({ children, onClusterClick }) {
	const map = useMap();
	const [markers, setMarkers] = useState({});
	const clusterer = useRef(null);

	// Initialize the MarkerClusterer
	useEffect(() => {
		if (!map) return;

		if (!clusterer.current) {
			clusterer.current = new GoogleMarkerClusterer({
				map,
				algorithmOptions: {
					maxZoom: 15, // Don't cluster at zoom level 15 and above
				},
				renderer: {
					render: ({ count, position }) => {
						// Custom cluster marker rendering
						return new google.maps.Marker({
							position,
							icon: {
								url: `data:image/svg+xml;base64,${btoa(`
									<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
										<circle cx="25" cy="25" r="22" fill="#ef4444" stroke="#b91c1c" stroke-width="3"/>
										<text x="25" y="25" text-anchor="middle" dy=".3em" fill="white" font-family="Arial, sans-serif" font-size="16" font-weight="bold">${count}</text>
									</svg>
								`)}`,
								scaledSize: new google.maps.Size(50, 50),
								anchor: new google.maps.Point(25, 25),
							},
							zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
						});
					},
				},
				onClusterClick: (event, cluster, map) => {
					if (onClusterClick) {
						onClusterClick(event, cluster, map);
					}
					// Default behavior: zoom in on cluster click
					map.fitBounds(cluster.bounds);
				},
			});
		}
	}, [map, onClusterClick]);

	// Update markers when children change
	useEffect(() => {
		clusterer.current?.clearMarkers();
		clusterer.current?.addMarkers(Object.values(markers));
	}, [markers]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			clusterer.current?.clearMarkers();
		};
	}, []);

	// Provide setMarkers to children via callback
	const setMarkerCallback = useCallback((marker, key) => {
		setMarkers((prevMarkers) => {
			if (marker === null) {
				// Remove marker
				const newMarkers = { ...prevMarkers };
				delete newMarkers[key];
				return newMarkers;
			}
			// Add or update marker
			return { ...prevMarkers, [key]: marker };
		});
	}, []);

	return <>{typeof children === 'function' ? children(setMarkerCallback) : children}</>;
}

MarkerClusterer.propTypes = {
	children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
	onClusterClick: PropTypes.func,
};
